import { clamp } from "lodash-es";
import type { Ref } from "vue";
import { computed, onActivated, watch } from "vue";

import type { Board } from "@/model/board";
import { useZoomStore } from "@/store/zoom";

const legendZoomLimit = 2;
const recalcDelay = 10;

export function usePlanningLegend(board: Board, active: Ref<boolean>) {
  const showLegend = computed(() => active.value && useZoomStore().factor > legendZoomLimit);

  onActivated(() => {
    delayedRecalc();
  });

  watch(
    () => useZoomStore().factor,
    (_, old) => {
      if (active.value) {
        setTimeout(() => {
          if (old && old <= legendZoomLimit) {
            recalcLegendFontSizes();
          }
        }, recalcDelay);
      }
    },
    { immediate: true },
  );

  watch(() => board, delayedRecalc);

  function delayedRecalc() {
    setTimeout(() => {
      recalcLegendFontSizes();
      recalcHeadingFontSizes();
    }, recalcDelay);
  }

  function recalcLegendFontSizes() {
    if (useZoomStore().factor > legendZoomLimit) {
      recalcFontSize({
        selector: ".planning-board .iter-legend, .planning-board .group-legend",
        minSize: 30,
        maxSize: 80,
      });
    }
  }

  function recalcHeadingFontSizes() {
    const idealWidth = 0.8; // Ideally, font should be adjusted so title width is 80% of the container
    recalcFontSize({
      selector: ".planning-board .group-field .fit-font",
      minSize: 30,
      maxSize: 100,
      idealWidth,
    });
    recalcFontSize({
      selector: ".planning-board div.top-text .fit-font",
      minSize: 30,
      maxSize: 100,
      idealWidth,
    });
  }

  function recalcFontSize({
    selector,
    minSize,
    maxSize,
    idealWidth = 1,
  }: {
    selector: string;
    minSize: number;
    maxSize: number;
    idealWidth?: number;
  }) {
    const els = document.querySelectorAll<HTMLElement>(selector);

    if (els.length > 0) {
      let minQ = 1000;
      els.forEach((el) => {
        el.style.fontSize = "100%";
        el.childNodes.forEach((n) => {
          if (n.nodeType !== Node.COMMENT_NODE && n.nodeName !== "SVG") {
            const e = n as HTMLElement;
            minQ = Math.min(minQ, idealWidth * (e.offsetWidth / e.scrollWidth));
          }
        });
      });
      const size = clamp(100 * minQ, minSize, maxSize);
      els.forEach((el) => {
        el.classList.toggle("min-size", size === minSize);
        el.style.fontSize = size + "%";
      });
    }
  }

  return { showLegend };
}
